import React, { useEffect, useRef, useState } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import PythonIcon from "@/assets/images/python.svg?react";
import type { PersonalAccessToken } from "target_wasm";
import { CodeBlock } from "components/common/CodeBlock";
import { Fold } from "components/common/Fold";
import { SearchBar } from "components/common/SearchBar";
import { SectionRule } from "components/common/SectionRule";
import { MetaAddButton } from "components/main/datasets/DatasetMetaSection";
import { ListCount } from "components/main/pages/pageColumn";
import { HintRow } from "components/main/overview/ListEmptyShell";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { deleteToken, revokeToken } from "./TokensApi";
import { MAX_LIST_ROWS } from "./utils";
import { OverlayScroll } from "components/common/OverlayScroll";
import { ROW_GRID, TokenRow } from "./TokenRow";

const PYTHON_QUICKSTART = `pip install pymycorr
export MYCORR_API_TOKEN=<your token>`;
const HEADERS = ["Name", "Scope", "Created", "Expires"];
type View = "active" | "inactive";
const newestFirst = (a: PersonalAccessToken, b: PersonalAccessToken) =>
  Number(b.get_created_at - a.get_created_at);

interface TokenListProps {
  tokens: PersonalAccessToken[];
  onGenerate: () => void;
  onChanged: () => Promise<unknown>;
  highlightId?: string | null;
}

export const TokenList: React.FC<TokenListProps> = ({
  tokens,
  onGenerate,
  onChanged,
  highlightId,
}) => {
  const [query, setQuery] = useState("");
  const [pythonOpen, setPythonOpen] = useState(false);
  const [view, setView] = useState<View>("active");
  const [confirming, setConfirming] = useState<string | null>(null);
  // A new token flashes once, not again each time its row comes back into a list.
  const [flashId, setFlashId] = useState(highlightId);
  const [lastHighlightId, setLastHighlightId] = useState(highlightId);
  if (highlightId !== lastHighlightId) {
    setLastHighlightId(highlightId);
    setFlashId(highlightId);
  }

  const active = tokens.filter((t) => t.get_status === "active");
  const inactive = tokens.filter((t) => t.get_status !== "active");
  // Removing the last expired or revoked token empties that list: back to active.
  if (view === "inactive" && inactive.length === 0) setView("active");
  const shown = view === "inactive" ? inactive : active;
  const searchable = shown.length > MAX_LIST_ROWS;
  // A list too short to search ignores a query typed into the other one.
  const q = searchable ? query.trim().toLowerCase() : "";
  const list = shown
    .filter((t) => !q || t.get_description.toLowerCase().includes(q))
    .sort(newestFirst);
  const scroller = useRef<HTMLDivElement>(null);
  // A new search, view or token starts the list at its top.
  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = 0;
  }, [q, view, highlightId]);

  const switchView = (to: View) => {
    setView(to);
    setConfirming(null);
    setFlashId(null);
  };
  const viewCount = (to: View, label: string) => (
    // eslint-disable-next-line no-restricted-syntax -- a footer count that switches the list, styled as the count
    <button
      type="button"
      aria-pressed={view === to}
      className={`cursor-pointer ${view === to ? "text-fg" : "hover:text-fg"}`}
      onClick={() => switchView(to)}
    >
      {label}
    </button>
  );
  const act = async (
    run: () => Promise<void>,
    done: string,
    failed: string,
  ) => {
    try {
      await run();
      showNotificationGlobal(done, "success");
      await onChanged();
    } catch {
      showNotificationGlobal(failed, "error");
    }
  };
  const revoke = (id: string) => {
    setConfirming(null);
    void act(() => revokeToken(id), "Token revoked", "Failed to revoke token");
  };
  const remove = (id: string) =>
    void act(() => deleteToken(id), "Removed", "Failed to remove token");

  const row = (t: PersonalAccessToken) => (
    <TokenRow
      key={t.get_token_id}
      token={t}
      confirming={confirming === t.get_token_id}
      onConfirming={(open) => setConfirming(open ? t.get_token_id : null)}
      onRevoke={() => revoke(t.get_token_id)}
      onRemove={() => remove(t.get_token_id)}
      highlighted={flashId === t.get_token_id}
      onHighlightEnd={() => setFlashId(null)}
    />
  );

  return (
    <section>
      <SectionRule
        inset
        icon={<VpnKeyOutlinedIcon sx={{ fontSize: 14 }} />}
        label="Personal access tokens"
      />
      <div className="flex flex-col gap-3">
        <div>
          {/* eslint-disable-next-line no-restricted-syntax -- a full-width disclosure row, not a Button-styled control */}
          <button
            type="button"
            aria-expanded={pythonOpen}
            className="flex w-full items-center gap-2 item-height px-2 rounded-app cursor-pointer text-fg-medium hover:bg-bg-hover"
            onClick={() => setPythonOpen((v) => !v)}
          >
            <PythonIcon width={16} height={16} aria-hidden />
            Use a token with Python
            <ChevronRightIcon
              className="icon-sm ml-auto text-fg-muted"
              style={{
                rotate: pythonOpen ? "90deg" : "0deg",
                transition: "rotate var(--duration-short) var(--ease-move)",
              }}
            />
          </button>
          <Fold open={pythonOpen}>
            <div className="flex flex-col gap-2 px-2 pt-1 pb-2">
              <p className="text-fg-muted-regular m-0">
                Create a token, then run:
              </p>
              <CodeBlock
                code={PYTHON_QUICKSTART}
                ariaLabel="Shell: install pymycorr and set the token"
                className="bg-bg! border-border select-text"
                onCopy={() => showNotificationGlobal("Copied", "success")}
              />
            </div>
          </Fold>
        </div>

        {searchable && (
          <SearchBar
            value={query}
            onChange={setQuery}
            placeholder="Search tokens"
            inputProps={{ "aria-label": "Search tokens" }}
            onKeyDown={(e) => {
              if (e.key === "Escape") setQuery("");
            }}
          />
        )}

        {list.length === 0 ? (
          <HintRow>
            {q
              ? `No token matches “${query.trim()}”.`
              : "No active tokens yet. Create one to use MyCorr from a script or an agent."}
          </HintRow>
        ) : (
          <div role="table" aria-label="Personal access tokens">
            <div
              role="row"
              className={`${ROW_GRID} h-(--control-height-md) font-mono uppercase tracking-[0.06em] text-fg-muted max-sm:hidden`}
              style={{ fontSize: "var(--font-size-x-small)" }}
            >
              {HEADERS.map((h) => (
                <span key={h} role="columnheader">
                  {h}
                </span>
              ))}
              <span role="columnheader" className="sr-only">
                Actions
              </span>
            </div>
            <OverlayScroll
              scrollRef={scroller}
              data-testid="token-rows"
              style={{
                maxHeight: `calc(${MAX_LIST_ROWS} * var(--control-height-lg))`,
              }}
            >
              {list.map(row)}
            </OverlayScroll>
          </div>
        )}

        <div className="mt-3 flex items-center">
          <ListCount
            parts={
              inactive.length > 0
                ? [
                    viewCount("active", `${active.length} active`),
                    viewCount(
                      "inactive",
                      `${inactive.length} expired or revoked`,
                    ),
                  ]
                : [active.length > 0 && `${active.length} active`]
            }
          />
          <span className="ml-auto">
            <MetaAddButton label="New token" onClick={onGenerate} />
          </span>
        </div>
      </div>
    </section>
  );
};
